import React, { useEffect, useRef } from 'react';

import cx from 'classnames';
import { isEmpty, isNil } from 'ramda';
import { createPortal } from 'react-dom';
import OutsideClickHandler from 'react-outside-click-handler';
import { useDispatch, useSelector } from 'react-redux';

import Preview from 'components/Pages/Admin/shared/Preview';
import Icon from 'components/shared/Icon';
import { NoOptions } from 'components/shared/Icon/icons/NoOptions';
import Plus from 'components/shared/Icon/icons/Plus';
import Visibility from 'components/shared/Icon/icons/Visibility';
import MenuActionsHelper from 'components/shared/MenuActionsHelper';
import Modal from 'components/shared/Modal';
import { ContentIndicator } from 'components/shared/Select/components/ContentIndicator';
import Tooltip, { TooltipText } from 'components/shared/Tooltip';
import Text from 'components/shared/Typography/Text';
import { CREATE_ACTIVITY_TOOLTIP_MSG } from 'constants/texts';
import { closeModalAction, openModalAction } from 'store/modal';
import { isModalOpenedSelector, modalDataSelector } from 'store/modal/selectors';
import { setAppHasChangesAction } from 'store/unsavedChanges';
import { appHasChangesSelector } from 'store/unsavedChanges/selectors';
import { canCreateNewContentPageSelector, isAgentSelector } from 'store/user/selectors';
import noop from 'utils/noop';

import { openContentCreationModal } from '../../DetailsChronologicalMode/Event/ModalContents/Edit/shared/ContentCreationModal';
import { EDITOR_TYPES, mentionSuggestionsElementId } from '../constants';
import { EDITING_BLOCK_TYPES, getNormalizedPositionStyles, useEditorContext } from '../context';
import styles from './index.module.css';

function Suggestion({ suggestion, onClick, focused, onPreviewClick }) {
  const isAgent = useSelector(isAgentSelector);
  const canShowPreview = suggestion.currentStep === 'page';
  const containerRef = useRef();
  const shouldShowSPContentIndicator = isAgent && (suggestion.isSafariPortalContent || suggestion?.shared);

  return (
    <div
      ref={containerRef}
      key={suggestion.id}
      className={cx(styles.suggestion, focused && styles.suggestionFocused)}
      onClick={() => {
        onClick(suggestion);
      }}
    >
      <div>
        <div className={styles.suggestionName}>{suggestion.name}</div>
        <div className={styles.suggestionTags}>{suggestion.tags}</div>
      </div>
      {canShowPreview && (
        <Icon
          component={Visibility}
          width="14px"
          height="10px"
          className={styles.previewButton}
          onClick={(event) => {
            event.stopPropagation();
            onPreviewClick(suggestion.id);
          }}
        />
      )}
      {shouldShowSPContentIndicator && <ContentIndicator isSharedPage={suggestion?.shared} />}
    </div>
  );
}

function scrollTo(el, top) {
  el.scrollTop = top;
}
function scrollIntoView(menuEl, focusedIdx) {
  const menuRect = menuEl.getBoundingClientRect();
  const focusedEl = menuEl.children[focusedIdx];

  if (!focusedEl) {
    return;
  }

  const focusedRect = focusedEl.getBoundingClientRect();
  const overScroll = focusedEl.offsetHeight / 3;

  if (focusedRect.bottom + overScroll > menuRect.bottom) {
    scrollTo(
      menuEl,
      Math.min(focusedEl.offsetTop + focusedEl.clientHeight - menuEl.offsetHeight + overScroll, menuEl.scrollHeight),
    );
  } else if (focusedRect.top - overScroll < menuRect.top) {
    scrollTo(menuEl, Math.max(focusedEl.offsetTop - overScroll, 0));
  }
}

function MentionSuggestions({ customPageType }) {
  const {
    editingBlockState,
    resetEditingBlockState,
    suggestions,
    isLoadingSuggestions,
    mountEl,
    handlePickSuggestion,
    focusedSuggestionIdx,
    handleOpenCreateNewPage,
    focusEditor,
  } = useEditorContext();
  const appHasChanges = useSelector(appHasChangesSelector);
  const isCreatable = useSelector(canCreateNewContentPageSelector);

  const dispatch = useDispatch();
  const containerRef = useRef();
  const listRef = useRef();

  const handleCreateNewPage = async () => {
    const initAppHasChanges = appHasChanges;
    // setAppChanges required because the page and the modal watch for one store
    dispatch(setAppHasChangesAction(false));

    const handleModalRes = (res) => {
      dispatch(setAppHasChangesAction(initAppHasChanges));
      if (!res) return;
      if (res === 'one-off') {
        focusEditor();
        if (customPageType === EDITOR_TYPES.ACCOMMODATION) return;

        // waiting when the editor will be focused
        return setTimeout(() => handleOpenCreateNewPage());
      }

      handlePickSuggestion(res);
      dispatch(setAppHasChangesAction(true));
    };

    const initialName = editingBlockState.data?.query;

    if (customPageType === EDITOR_TYPES.ACCOMMODATION) {
      const res = await openContentCreationModal({
        type: 'property',
        initialValues: { name: initialName },
        withoutOneOff: true,
      });
      handleModalRes(res);
    } else {
      const res = await openContentCreationModal({ type: 'default', initialValues: { name: initialName } });
      handleModalRes(res);
    }
  };

  const closeModal = () => {
    dispatch(closeModalAction());
  };
  const openModal = (pageId) => {
    dispatch(openModalAction({ type: 'PAGE_PREVIEW', data: { pageId } }));
  };
  const isPreviewModalOpen = useSelector(isModalOpenedSelector('PAGE_PREVIEW'));
  const modalData = useSelector(modalDataSelector);

  useEffect(() => {
    if (!listRef.current || isNil(focusedSuggestionIdx) || isEmpty(suggestions)) {
      return;
    }

    scrollIntoView(listRef.current, focusedSuggestionIdx);
  }, [focusedSuggestionIdx]);

  if (!editingBlockState.isOpen || editingBlockState.blockType !== EDITING_BLOCK_TYPES.SUGGESTIONS) {
    return null;
  }

  return (
    <>
      {createPortal(
        <OutsideClickHandler onOutsideClick={isPreviewModalOpen ? noop : resetEditingBlockState}>
          <div
            id={mentionSuggestionsElementId}
            style={getNormalizedPositionStyles(editingBlockState.blockRect, mountEl, containerRef.current)}
            className={styles.container}
            ref={containerRef}
          >
            <div className={styles.suggestionsList} ref={listRef}>
              {isLoadingSuggestions && <div className={styles.loading}>Loading...</div>}
              {!isLoadingSuggestions && isEmpty(suggestions) && (
                <div className={styles.noData}>
                  <Icon component={NoOptions} />
                  <span>No matches found</span>
                </div>
              )}
              {!isLoadingSuggestions &&
                suggestions.map((suggestion, idx) => (
                  <Suggestion
                    key={suggestion.id}
                    suggestion={suggestion}
                    onClick={handlePickSuggestion}
                    onPreviewClick={openModal}
                    focused={focusedSuggestionIdx === idx}
                  />
                ))}
            </div>

            <>
              {!isCreatable && (
                <Tooltip place="top" id="create-tooltip" withMouseOver>
                  <TooltipText>
                    <div className={styles.tooltipWrapper}>
                      <p className={styles.tooltipText}>{CREATE_ACTIVITY_TOOLTIP_MSG}</p>
                    </div>
                  </TooltipText>
                </Tooltip>
              )}

              <button
                className={styles.addNewPageButton}
                type="button"
                disabled={!isCreatable}
                onClick={handleCreateNewPage}
                data-tip
                data-for="create-tooltip"
              >
                <Plus width="20" height="20" fill="currentColor" />
                <div className={styles.addNewPageButtonLabel}>
                  <Text type="brandon" size="medium">
                    Create New Page
                  </Text>
                </div>
              </button>

              {customPageType !== EDITOR_TYPES.ACCOMMODATION && (
                <button className={styles.addNewPageButton} type="button" onClick={handleOpenCreateNewPage}>
                  <Plus width="20" height="20" fill="currentColor" />
                  <div className={styles.addNewPageButtonLabel}>
                    <Text type="brandon" size="medium">
                      Create One-Off Placeholder Event
                    </Text>
                  </div>
                </button>
              )}
            </>

            <MenuActionsHelper />
          </div>
        </OutsideClickHandler>,
        mountEl,
      )}

      <Modal noPadding size="lg" isOpen={isPreviewModalOpen} toClose={closeModal}>
        {isPreviewModalOpen && <Preview pageId={modalData.pageId} />}
      </Modal>
    </>
  );
}

export default MentionSuggestions;
